import React from "react";
import financeV2Adapters from "../../../../adapters/financeV2Adapters";
import tools from "../../../../helpers/tools";
import styles from "../FinanceDetailV2.module.scss";

/**
 * Sélecteur d'enveloppe pour une demande de financement.
 *
 * Les enveloppes sont groupées par financeur. Seules les enveloppes ACTIVES
 * (`estActive`) sont sélectionnables. Chaque carte est « capacity-aware » au
 * niveau de l'enveloppe : disponible propre + impact « après cette demande ».
 *
 * @param groupes  { [financeurUuid]: { financeurNom, consommation, enveloppes:[…] } }
 */
const EnveloppeSelector = ({
  groupes = {},
  selectedEnveloppeUuid,
  totalEnvelop = 0,
  derniereEcheance = null,
  enveloppeDejaImputee = null,
  disabled = false,
  onSelect,
}) => {
  const handleSelect = (uuid) => {
    if (disabled) return;
    onSelect(uuid === selectedEnveloppeUuid ? "" : uuid);
  };

  const renderCard = (env) => {
    const origine = env.consommation.enveloppeOrigine || env.montant || 0;
    const disponible = env.consommation.disponible;
    const consomme = env.consommation.reserve + env.consommation.engage;
    const ratio = financeV2Adapters.enveloppeRatio(consomme, origine);
    const color = financeV2Adapters.enveloppeColor(ratio);
    // La jauge représente le DISPONIBLE (pleine = beaucoup de capacité).
    const dispoRatio = financeV2Adapters.enveloppeRatio(disponible, origine);

    const isSelected = env.uuid === selectedEnveloppeUuid;
    // Enveloppe déjà imputée à cette demande : son investissement est déjà
    // compté dans le disponible → ne pas le resoustraire (sinon double-comptage).
    const dejaImpute = enveloppeDejaImputee === env.uuid;
    const apres = disponible - totalEnvelop;
    const isDateMaximaleDepassee = financeV2Adapters.enveloppeDateMaximaleDepassee(
      env.dateMaximale,
      derniereEcheance,
    );
    const isInsufficient =
      origine <= 0 ||
      isDateMaximaleDepassee ||
      (!dejaImpute && totalEnvelop > 0 && apres < 0);
    const typeLabel =
      financeV2Adapters.FINANCE_TYPE_MAP[env.typeFinancement] ||
      env.typeFinancement ||
      "Type non défini";

    let status;
    if (origine <= 0) {
      status = (
        <div className={styles.financierCardStatusBad}>
          <i className="fa fa-exclamation-triangle" />
          Enveloppe épuisée
        </div>
      );
    } else if (isDateMaximaleDepassee) {
      status = (
        <div className={styles.financierCardStatusBad}>
          <i className="fa fa-exclamation-triangle" />
          Dépasse la date maximale ({tools.formatDate(env.dateMaximale)})
        </div>
      );
    } else if (dejaImpute) {
      status = (
        <div className={styles.financierCardStatusMuted}>
          Déjà imputé à cette demande
        </div>
      );
    } else if (totalEnvelop > 0 && apres < 0) {
      status = (
        <div className={styles.financierCardStatusBad}>
          <i className="fa fa-exclamation-triangle" />
          Dépasse de {Math.abs(apres).toLocaleString("fr-FR")} €
        </div>
      );
    } else if (totalEnvelop > 0) {
      status = (
        <div className={styles.financierCardStatusOk}>
          <i className="fa fa-check-circle" />
          Après cette demande : {apres.toLocaleString("fr-FR")} €
        </div>
      );
    } else {
      status = (
        <div className={styles.financierCardStatusMuted}>Disponible</div>
      );
    }

    return (
      <div
        key={env.uuid}
        role="radio"
        aria-checked={isSelected}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        className={[
          styles.financierCard,
          isSelected ? styles.financierCardSelected : "",
          isInsufficient ? styles.financierCardInsufficient : "",
          disabled ? styles.financierCardDisabled : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => handleSelect(env.uuid)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleSelect(env.uuid);
          }
        }}
        title={
          isSelected
            ? "Cliquer pour désélectionner"
            : `Choisir l'enveloppe ${env.nom || ""}`
        }
      >
        <div className={styles.financierCardTop}>
          <span className={styles.financierCardName}>
            {env.nom || "Enveloppe"}
          </span>
          {isSelected && (
            <span className={styles.financierCardCheck}>
              <i className="fa fa-check" />
            </span>
          )}
        </div>
        <div className={styles.enveloppeCardMeta}>{typeLabel}</div>
        <div className={styles.financierCardCaption}>Disponible</div>
        <div className={styles.financierCardAmount}>
          {Number(disponible).toLocaleString("fr-FR")} €
        </div>
        <div className={styles.financierCardGauge}>
          <div
            className={styles.financierCardGaugeFill}
            style={{ width: `${dispoRatio}%`, background: color }}
          />
        </div>
        {status}
      </div>
    );
  };

  const groupeEntries = Object.entries(groupes);
  if (groupeEntries.length === 0) {
    return (
      <div className={styles.financierCardStatusMuted}>
        Aucun financeur disponible.
      </div>
    );
  }

  return (
    <div role="radiogroup" aria-label="Enveloppe">
      {groupeEntries.map(([finUuid, g]) => {
        const actives = (g.enveloppes || []).filter((e) => e.estActive);
        if (actives.length === 0) return null;
        const cumulDispo = g.consommation?.disponible ?? 0;
        return (
          <div key={finUuid} className={styles.enveloppeGroup}>
            <div className={styles.enveloppeGroupHead}>
              <span className={styles.enveloppeGroupName}>
                {g.financeurNom}
              </span>
              <span className={styles.enveloppeGroupCumul}>
                Dispo. cumulé&nbsp;: {Number(cumulDispo).toLocaleString("fr-FR")} €
              </span>
            </div>
            <div className={styles.financierGrid}>
              {actives.map((env) => renderCard(env))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EnveloppeSelector;
